import { Button, Dropdown, Image, Input, Select, Space, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import CaretDownIcon from "../../../../assets/icons/CaretDownIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import ThreeDotsIcon from "../../../../assets/icons/ThreeDotsIcon";
import placeholder from "../../../../assets/images/placeholder.png";
import ActionModal from "../../../../components/ui/ActionModal";
import CustomTable from "../../../../components/ui/CustomTable";
import {
  useGetAllProductsQuery,
  useUpdateProductStatusMutation,
} from "../../../../features/superAdmin/ticketService/product/product.api";
import { ProductDetails } from "../../../../features/superAdmin/ticketService/product/product.types";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import ManageProduct from "./_components/ManageProduct";

const AllProducts = () => {
  const {
    page,
    limit,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable({
    defaultFilter: { status: "all" },
  });
  const { open, handleOpen, handleClose } = useModal<unknown, "DELETE">();

  const { data, isLoading, isError, error } = useGetAllProductsQuery({
    params: {
      page,
      limit,
      search: keyword,
      status: filter.status === "all" ? undefined : filter.status,
    },
  });

  // ------ Mutations ------
  const [
    updateStatus,
    {
      isLoading: updating,
      isError: isUpdateError,
      error: updateError,
      isSuccess,
    },
  ] = useUpdateProductStatusMutation();

  // ------ Effects ------
  useEffect(() => {
    if (!updating && isSuccess) {
      toast("success", "Product status updated.");
    }
  }, [updating, isSuccess]);

  // ------ Errors ------
  useError(isError, error, "Product get failed.");
  useError(isUpdateError, updateError, "Product get failed.");

  const columns: ColumnsType<ProductDetails> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      dataIndex: "logo",
      title: "Logo",
      render: (val) => {
        return (
          <Image
            src={val || placeholder}
            width={40}
            height={40}
            preview={false}
            className="rounded-md !object-contain"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = placeholder;
            }}
          />
        );
      },
    },
    {
      key: "name",
      dataIndex: "title",
      title: <span className="text-nowrap">Product Title</span>,
      render: (val) => (val ? val : "N/A"),
    },
    {
      key: "description",
      dataIndex: "description",
      title: <span className="text-nowrap">Product Description</span>,
      width: 400,
      render: (val) => (val ? <span className="w-[400px]">{val}</span> : "N/A"),
    },
    {
      key: "status",
      dataIndex: "status",
      title: "Status",
      align: "center" as const,
      render: (status) => (
        <div
          className={`status ${
            status.toUpperCase() === "ACTIVE"
              ? "status-success"
              : "status-danger"
          }`}
        >
          {capitalize(status)}
        </div>
      ),
    },
    {
      key: "action",
      title: "Action",
      align: "center" as const,
      render: (data: any) => {
        const isActive = data?.status?.toUpperCase() === "ACTIVE";
        return (
          <div className="flex items-center justify-center">
            <Dropdown
              overlayClassName="w-36"
              menu={{
                items: [
                  {
                    key: "0",
                    label: (
                      <Space className="flex items-center justify-between">
                        <span className="text-sm font-medium">Active</span>
                        <Switch size="default" checked={isActive} />
                      </Space>
                    ),
                    onClick: () => {
                      updateStatus({
                        id: data?.id,
                        data: { status: isActive ? "inactive" : "active" },
                      });
                    },
                  },
                  {
                    key: "2",
                    label: (
                      <Space className="flex items-center">
                        <EditIcon />
                        <span className="text-sm font-medium">Edit</span>
                      </Space>
                    ),
                    onClick: () => handleOpen({ type: "UPDATE", data: data }),
                  },
                  // {
                  //   key: "3",
                  //   label: (
                  //     <Space className="flex items-center">
                  //       <DeleteIcon />
                  //       <span className="text-sm font-medium">Delete</span>
                  //     </Space>
                  //   ),
                  //   onClick: () => handleOpen({ type: "DELETE", data: data }),
                  // },
                ],
              }}
              trigger={["click"]}
            >
              <div className="cursor-pointer">
                <ThreeDotsIcon />
              </div>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <SidebarLayout title="Product Management">
      <CustomTable
        className="designationTable"
        limit={limit}
        page={page}
        columns={columns}
        dataSource={data?.data?.products}
        total={data?.data?.totalLength || 0}
        loading={isLoading}
        topSectionClassName="w-full"
        filterSection={
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-2 items-center">
              <div>Total Rows</div>
              <Select
                placeholder="10"
                suffixIcon={<CaretDownIcon />}
                size="middle"
                style={{ width: 70 }}
                onChange={handleLimit}
                options={[
                  { label: "10", value: 10 },
                  { label: "20", value: 20 },
                  { label: "50", value: 50 },
                  { label: "100", value: 100 },
                  { label: "1000", value: 1000 },
                ]}
              />
              <div>of {data?.data?.totalLength}</div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search by title"
                suffix={<SearchIcon />}
                size="middle"
                className="max-w-[200px]"
                onChange={handleSearch}
              />
              <Select
                allowClear
                placeholder="Status"
                suffixIcon={<CaretDownIcon />}
                size="middle"
                style={{ width: 120 }}
                value={filter.status}
                onChange={(value) => handleFilter("status", value)}
                options={[
                  { label: "All", value: "all" },
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
              />
              <Button
                className="button-primary-light h-9"
                onClick={() => handleOpen({ type: "ADD", data: undefined })}
              >
                <AddIcon />
                Add Product
              </Button>
            </div>
          </div>
        }
      />

      <ManageProduct
        open={open.open && (open.type === "ADD" || open.type === "UPDATE")}
        data={open.data}
        handleClose={handleClose}
      />

      <ActionModal
        open={open?.open && open.type === "DELETE"}
        loading={false}
        title={"Do you want to delete this product?"}
        subTitle={
          "Are you sure you want to delete this product? This action cannot be."
        }
        iconType="danger"
        actionBtnText={`Yes, Delete`}
        actionBtnIcon
        actionBtnIconType="delete"
        actionBtnType="danger-light"
        cancelButton={false}
        handleClose={handleClose}
        handleAction={() => {}}
        handleCancel={handleClose}
      />
    </SidebarLayout>
  );
};

export default AllProducts;
